import React from 'react';
import { Box } from '@withjoy/joykit';
import type { AdminHeaderBarSurfaceProps } from './AdminHeaderBarSurface.types';

/**
 * Frosted-glass surface container for multi-tier sticky headers.
 *
 * Provides a unified stacking context so AdminHeaderBar and any secondary
 * content (search bars, mega-navs, mobile tab bars) scroll as one unit
 * with consistent background treatment.
 *
 * Render AdminHeaderBar inside with `position="static"` — the Surface
 * handles positioning and z-index for the whole group.
 *
 * @example
 * <AdminHeaderBar.Surface position="sticky">
 *   <AdminHeaderBar position="static" title="Registry" navigation={<RegistryTabNav />} />
 *   <Box paddingTop={4}>
 *     <SearchBar />
 *     <MegaNav />
 *   </Box>
 * </AdminHeaderBar.Surface>
 */
export const AdminHeaderBarSurface: React.FC<AdminHeaderBarSurfaceProps> = ({ children, position = 'sticky', 'data-testid': testId = 'admin-header-bar-surface' }) => (
  <Box
    as="div"
    position={position}
    top={position === 'sticky' ? 0 : undefined}
    zIndex="docked"
    width="100%"
    backgroundColor="rgba(255, 255, 255, 0.92)"
    backdropFilter="saturate(180%) blur(20px)"
    data-testid={testId}
  >
    {children}
  </Box>
);

AdminHeaderBarSurface.displayName = 'AdminHeaderBarSurface';
